/**
 * 今日の習慣リストコンポーネント
 *
 * ダッシュボードのメインセクション。
 * 今日予定されている習慣をチェックボックス付きで一覧表示する。
 *
 * Rails で言えば:
 * - app/views/dashboard/_today_habits.html.erb に相当
 *
 * @example
 * <TodayHabits
 *   habits={scheduledHabits}
 *   pendingToggles={{ 1: true }}
 *   onToggle={(id) => handleToggle(id)}
 * />
 */

import { Link } from '@inertiajs/react'
import { CheckSquare, ArrowRight } from 'lucide-react'
import { HabitCheckbox } from '@/components/habits/HabitCheckbox'
import { cn } from '@/lib/utils'
import { habits_path } from '@/lib/routes'
import type { TodayHabit } from '@/types'

interface TodayHabitsProps {
  /** 今日予定されている習慣リスト */
  habits: TodayHabit[]
  /** 現在トグル処理中の習慣ID（ローディング表示用） */
  pendingToggles: Record<number, boolean>
  /** 習慣の完了/未完了を切り替えるハンドラー */
  onToggle: (habitId: number) => void
}

export function TodayHabits({
  habits,
  pendingToggles,
  onToggle,
}: TodayHabitsProps) {
  return (
    <div className="bg-card border-border rounded-xl border p-4 sm:p-5">
      {/* ヘッダー */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-foreground flex items-center gap-2 font-semibold">
          <CheckSquare className="text-primary h-5 w-5" />
          今日の習慣
        </h2>
        <Link
          href={habits_path()}
          className="text-primary flex items-center gap-1 text-sm hover:underline"
        >
          すべて見る
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* 習慣リスト */}
      {habits.length > 0 ? (
        <div className="space-y-2">
          {habits.map((habit) => (
            <div
              key={habit.id}
              className={cn(
                'flex items-center gap-3 rounded-lg p-3 transition-colors',
                habit.completedToday
                  ? 'bg-muted/50'
                  : 'bg-muted/30 hover:bg-muted/50'
              )}
              style={{
                borderLeft: `3px solid ${habit.color || '#3b82f6'}`,
              }}
            >
              <HabitCheckbox
                checked={habit.completedToday}
                onClick={() => onToggle(habit.id)}
                pending={pendingToggles[habit.id]}
                color={habit.color ?? undefined}
                size="sm"
              />
              <span
                className={cn(
                  'flex-1 font-medium',
                  habit.completedToday && 'text-muted-foreground line-through'
                )}
              >
                {habit.name}
              </span>
            </div>
          ))}
        </div>
      ) : (
        // 習慣がない場合の空状態
        <div className="text-muted-foreground py-8 text-center">
          <p className="mb-4">今日予定されている習慣はありません</p>
          <Link
            href={habits_path()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2"
          >
            習慣を追加する
          </Link>
        </div>
      )}
    </div>
  )
}
